import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSurvey } from '../context/SurveyContext';
import { debugLog } from '../utils/logger';

/**
 * useGuardedRoute
 * Redirects to /validate if no session token is present.
 * Preserves the original ?pid= param so the user can re-verify
 * if they navigate directly to /preferences or the page refreshes.
 *
 * Usage: call at the top of any guarded page component.
 *   const isReady = useGuardedRoute();
 *   if (!isReady) return null;
 */
export function useGuardedRoute() {
    const { sessionToken } = useSurvey();
    const navigate         = useNavigate();
    const location         = useLocation();

    useEffect(() => {
        debugLog('ui_guarded_route_evaluated', {
            path: location.pathname,
            hasSessionToken: Boolean(sessionToken)
        });
        if (!sessionToken) {
            // Preserve any pid param from the current URL so the validate page
            // can still pick it up after the redirect
            const params = new URLSearchParams(location.search);
            const pid    = params.get('pid');
            const target = pid ? `/validate?pid=${pid}` : '/validate';
            debugLog('ui_guarded_route_redirecting', { target });
            navigate(target, { replace: true });
        }
    }, [sessionToken, navigate, location.search]);

    return !!sessionToken;
}
